import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Play, Pause, Stop, CheckCircle, Clock, Gear, Briefcase, ArrowClockwise, Warning,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/permissions";
import MyPerformance from "@/components/MyPerformance";
import EditableSelect from "@/components/EditableSelect";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const hhmmss = (hours) => {
  const total = Math.max(0, Math.round((Number(hours) || 0) * 3600));
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

function LiveTimer({ session }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const start = new Date(session.start_time).getTime();
  const paused = session.status === "Paused";
  const openPause = (session.pauses || []).find((p) => !p.end);
  const nowMs = paused && openPause ? new Date(openPause.start).getTime() : Date.now();
  const grossH = (nowMs - start) / 3600000;
  const approved = Number(session.approved_pause_hours) || 0;
  return (
    <div className="text-center">
      <div className={`text-5xl font-bold tabular-nums ${paused ? "text-amber-600" : "text-emerald-600"}`} data-testid="work-timer">
        {hhmmss(grossH - approved)}
      </div>
      <div className="text-xs text-slate-500 mt-1">
        {paused ? "Paused — timer held" : "Running"}
        {approved > 0 && ` · ${hhmmss(approved)} approved break excluded`}
      </div>
    </div>
  );
}

function AssignmentCard({ item, onStart, busy, isOperatorView }) {
  const done = item.completed;
  const running = !!item.active_session;
  return (
    <Card className={`p-4 border-slate-200 shadow-none ${done ? "opacity-60" : ""}`} data-testid={`work-item-${item.job_code}-${item.machine_code}-${item.operation_code}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <Briefcase className="h-4 w-4 text-blue-600" weight="bold" />
            <span className="font-mono text-blue-700">{item.job_code}</span>
            <span className="truncate">{item.job_name}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Gear className="h-3.5 w-3.5" /> {item.machine_code} · {item.machine_name}</span>
            <span>{item.operation_name}</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> Planned: operation {item.operation_standard_hours || 0} h · machine {item.machine_planned_hours || 0} h
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="outline" className="bg-slate-50 text-slate-600" data-testid={`work-task-hours-${item.job_code}-${item.machine_code}-${item.operation_code}`}>
              Your actual on this task {item.hours_logged} h across {item.sessions_count} session{item.sessions_count === 1 ? "" : "s"} · machine logged {item.machine_logged_hours ?? 0} h
            </Badge>
            {item.operation_standard_hours > 0 && item.hours_logged > 0 && (
              <Badge variant="outline" className={item.hours_logged <= item.operation_standard_hours
                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                : "bg-amber-100 text-amber-700 border-amber-200"}>
                {Math.min(Math.round((item.operation_standard_hours / item.hours_logged) * 100), 999)}% efficiency
              </Badge>
            )}
            {done && <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200">Operation complete</Badge>}
            {isOperatorView && !item.assigned && !done && (
              <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200">Not allocated to you</Badge>
            )}
            {running && (
              <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200">
                Running{item.active_operator_name ? ` · ${item.active_operator_name}` : ""}
              </Badge>
            )}
            {!isOperatorView && !done && (item.operators || []).length > 0 && (
              <span className="text-slate-500">Operators: {(item.operators || []).map((o) => o.name).join(", ")}</span>
            )}
            {!done && item.planned_hours > 0 && item.machine_logged_hours > item.planned_hours && (
              <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">Over planned hours</Badge>
            )}
          </div>
        </div>
        {!done && !running && (
          <Button onClick={() => onStart(item)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 shrink-0"
            data-testid={`work-start-${item.job_code}-${item.machine_code}-${item.operation_code}`}>
            <Play className="h-4 w-4 mr-1.5" weight="fill" /> {isOperatorView ? "Start" : "Start for operator"}
          </Button>
        )}
      </div>
    </Card>
  );
}

export default function LogProduction() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const admin = isAdmin(user?.role);
  const [busy, setBusy] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(null);
  const [pauseReason, setPauseReason] = useState("");
  const [pauseNote, setPauseNote] = useState("");
  const [ended, setEnded] = useState(null);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["work-assignments"],
    queryFn: async () => (await api.get("/work/assignments")).data,
    refetchInterval: 30000,
  });

  const items = data?.items || [];
  const { data: allTeam = [] } = useQuery({
    queryKey: ["entity", "team"],
    queryFn: async () => (await api.get("/team")).data,
    enabled: admin,
  });
  const isOperatorView = !!data?.operator_team_id;
  const unlinked = !!data && !data.operator_team_id && !admin;
  const activeList = items.filter((i) => i.active_session);
  const active = activeList[0]?.active_session || null;
  const activeItem = activeList[0] || null;
  const startable = items.filter((i) => !i.completed && !i.active_session);
  const [startFor, setStartFor] = useState(null);

  const after = async () => {
    await refetch();
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["entity"] });
    qc.invalidateQueries({ queryKey: ["work-approvals"] });
  };

  const call = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await after();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const start = (item, operatorId) => call(async () => {
    await api.post("/work/start", {
      job_id: item.job_id, machine_id: item.machine_id, operation_id: item.operation_id,
      ...(operatorId ? { operator_id: operatorId } : {}),
    });
    setStartFor(null);
    toast.success("Timer started");
  });

  const onStart = (item) => {
    setPickOpen(false);
    if (isOperatorView) return start(item);
    const ops = (item.operators || []).length ? item.operators : allTeam;
    if (ops.length === 1) return start(item, ops[0].id);
    if (ops.length === 0) { toast.error("No team members found — add them in Manage Users first"); return; }
    setStartFor({ ...item, operators: ops });
  };

  const doPause = () => {
    if (!pauseReason) { toast.error("Select a pause reason"); return; }
    const sid = pauseOpen;
    call(async () => {
      await api.post(`/work/sessions/${sid}/pause`, { reason: pauseReason, note: pauseNote });
      setPauseOpen(null); setPauseReason(""); setPauseNote("");
      toast.success("Paused — reason sent for manager approval");
    });
  };

  const resume = (sid) => call(async () => {
    await api.post(`/work/sessions/${sid}/resume`);
    toast.success("Resumed");
  });

  const end = (sid, item) => call(async () => {
    const { data: s } = await api.post(`/work/sessions/${sid}/end`);
    setEnded({ ...s, item });
    toast.success("Session ended");
  });

  const completeOperation = (item) => call(async () => {
    await api.post("/work/complete-operation", { job_id: item.job_id, machine_id: item.machine_id, operation_id: item.operation_id });
    setEnded(null);
    toast.success("Operation marked complete");
  });

  if (isLoading) return <div className="text-slate-400 py-20 text-center">Loading your work...</div>;

  if (ended) {
    return (
      <div className="max-w-xl mx-auto space-y-4" data-testid="work-ended">
        <Card className="p-8 text-center border-slate-200 shadow-none">
          <CheckCircle className="h-14 w-14 text-emerald-500 mx-auto" weight="fill" />
          <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Hours recorded automatically</div>
          <div className="text-5xl font-bold text-slate-900 mt-1 tabular-nums" data-testid="work-ended-hours">{ended.actual_hours} h</div>
          <div className="text-sm text-slate-500 mt-2">
            Worked {hhmmss(ended.gross_hours)} gross
            {ended.approved_pause_hours > 0 && ` · ${hhmmss(ended.approved_pause_hours)} approved break removed`}
            {ended.pending_pause_hours > 0 && ` · ${hhmmss(ended.pending_pause_hours)} break awaiting approval`}
          </div>
          {ended.long_shift_approval === "Pending" && (
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-left text-xs text-amber-800" data-testid="work-long-shift-warning">
              <Warning className="h-4 w-4 shrink-0 mt-0.5" weight="fill" />
              This session ran over 12 hours straight, so it needs manager approval before it counts.
            </div>
          )}
          <div className="mt-3 text-xs text-slate-400">Log {ended.production_code} created · awaiting manager approval</div>
        </Card>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" className="flex-1" onClick={() => setEnded(null)} data-testid="work-back-list">Back to my work</Button>
          {ended.item && (
            <Button className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={busy}
              onClick={() => completeOperation(ended.item)} data-testid="work-complete-operation">
              <CheckCircle className="h-4 w-4 mr-1.5" weight="bold" /> Operation complete
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            {isOperatorView ? "My Work Today" : "Shop Floor Timers"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isOperatorView
              ? "Start the clock on an assigned machine — hours are calculated for you."
              : "Start, pause or end a timer on behalf of an operator who cannot do it themselves."}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {startable.length > 0 && !(isOperatorView && active) && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setPickOpen(true)} data-testid="work-start-picker-btn">
              <Play className="h-4 w-4 mr-1.5" weight="fill" /> Start timer
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="work-refresh">
            <ArrowClockwise className="h-4 w-4" />
          </Button>
          {admin && (
            <Button variant="outline" size="sm" onClick={() => navigate("/production")} data-testid="work-manual-entry">
              Manual entry
            </Button>
          )}
        </div>
      </div>

      {isOperatorView && <MyPerformance />}

      {unlinked && (
        <Card className="p-4 border-amber-200 bg-amber-50 shadow-none" data-testid="work-unlinked-warning">
          <p className="text-sm text-amber-900 font-semibold">Your login is not linked to a Team Member yet</p>
          <p className="text-xs text-amber-800 mt-1">
            Timers cannot start until your Team Member record exists. Ask a Manager to open Manage Users and save your
            account, then sign out and back in.
          </p>
        </Card>
      )}

      {isOperatorView && active && activeItem && active.auto_started && (
        <Card className="border-amber-300 bg-amber-50 shadow-none p-4" data-testid="work-auto-start-banner">
          <div className="flex items-start gap-2">
            <Warning className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" weight="fill" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-900">Timer auto-started</div>
              <p className="text-xs text-amber-800 mt-0.5">
                Your break crossed the {data?.grace_minutes ?? 30}-minute limit, so the clock was started at{" "}
                {new Date(active.start_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} on{" "}
                {activeItem.machine_code} · {activeItem.operation_name}. Working on something else?
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="h-7 border-amber-300 text-amber-800"
                  onClick={() => setSwitchOpen(true)} data-testid="work-switch-task-btn">
                  Choose a different task
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-amber-800" disabled={busy}
                  onClick={() => call(async () => { await api.post(`/work/sessions/${active.id}/acknowledge-auto`); })}
                  data-testid="work-auto-ack">
                  This is correct
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {activeList.map((it) => {
        const s = it.active_session;
        return (
        <Card key={s.id} className="border-emerald-200 bg-emerald-50/40 shadow-none p-5" data-testid="work-active-session">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                In progress{!isOperatorView && it.active_operator_name ? ` · ${it.active_operator_name}` : ""}
              </div>
              <div className="text-sm font-semibold text-slate-900 truncate">
                {it.job_code} · {it.machine_code} · {it.operation_name}
              </div>
              <div className="text-xs text-slate-500">Machine planned {it.machine_planned_hours || 0} h · session {s.code}</div>
            </div>
            <Badge variant="outline" className={s.status === "Paused" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}>
              {s.status}
            </Badge>
          </div>

          <LiveTimer session={s} />

          {s.pending_pause_hours > 0 && (
            <div className="mt-3 text-center text-xs text-amber-700" data-testid="work-pending-pause">
              {hhmmss(s.pending_pause_hours)} of breaks awaiting manager approval
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            {s.status === "Running" ? (
              <Button variant="outline" className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                disabled={busy} onClick={() => setPauseOpen(s.id)} data-testid="work-pause-btn">
                <Pause className="h-4 w-4 mr-1.5" weight="fill" /> Pause
              </Button>
            ) : (
              <Button variant="outline" className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                disabled={busy} onClick={() => resume(s.id)} data-testid="work-resume-btn">
                <Play className="h-4 w-4 mr-1.5" weight="fill" /> Resume
              </Button>
            )}
            <Button className="flex-1 bg-slate-900 hover:bg-slate-800" disabled={busy}
              onClick={() => end(s.id, it)} data-testid="work-end-btn">
              <Stop className="h-4 w-4 mr-1.5" weight="fill" /> End &amp; save hours
            </Button>
          </div>
          {s.status === "Running" && !(s.pauses || []).length && startable.length > 0 && (
            <button type="button" onClick={() => setSwitchOpen(true)} disabled={busy}
              className="mt-3 w-full text-center text-xs text-blue-600 hover:text-blue-800"
              data-testid="work-switch-task-inline">
              Working on a different job line? Switch this timer's task
            </button>
          )}
        </Card>
        );
      })}

      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {isOperatorView ? "Work you can clock on to" : "All job lines"} ({items.length})
        </div>
        {items.length === 0 ? (
          <Card className="p-8 text-center text-sm text-slate-400 border-slate-200 shadow-none" data-testid="work-empty">
            {isOperatorView
              ? "No active jobs right now. Ask your manager to set a job to Active."
              : "No active jobs right now — set a job to Active in Job Master."}
          </Card>
        ) : (
          items.map((i) => (
            <div key={`${i.job_id}-${i.machine_id}-${i.operation_id}`} className="space-y-2">
              <AssignmentCard item={i} onStart={onStart} busy={busy || (isOperatorView && !!active)} isOperatorView={isOperatorView} />
              {!i.completed && i.hours_logged > 0 && !i.active_session && (
                <button onClick={() => completeOperation(i)} disabled={busy}
                  className="text-xs text-blue-600 hover:text-blue-800 ml-1"
                  data-testid={`work-complete-${i.job_code}-${i.machine_code}-${i.operation_code}`}>
                  Mark this operation complete
                </button>
              )}
            </div>
          ))
        )}
      </div>

      <Dialog open={pickOpen} onOpenChange={setPickOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Which job line are you starting?</DialogTitle>
            <DialogDescription>
              Pick any open line — {startable.length} available right now.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
            {startable.map((i) => (
              <button key={`pick-${i.job_id}-${i.machine_id}-${i.operation_id}`} type="button" disabled={busy}
                onClick={() => onStart(i)}
                className="w-full text-left px-1 py-3 hover:bg-slate-50 disabled:opacity-50"
                data-testid={`work-pick-${i.job_code}-${i.machine_code}-${i.operation_code}`}>
                <div className="text-sm font-medium text-slate-900">
                  <span className="font-mono text-blue-700">{i.job_code}</span> · {i.machine_code} · {i.operation_name}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {i.job_name} · machine planned {i.machine_planned_hours || 0} h · logged {i.machine_logged_hours ?? 0} h
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickOpen(false)} data-testid="work-pick-cancel">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!startFor} onOpenChange={(v) => !v && setStartFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Start for which operator?</DialogTitle>
            <DialogDescription>
              {startFor && `${startFor.machine_code} · ${startFor.operation_name} on ${startFor.job_code}`}
            </DialogDescription>
          </DialogHeader>
          <div className="divide-y divide-slate-100">
            {(startFor?.operators || []).map((o) => (
              <button key={o.id} type="button" disabled={busy} onClick={() => start(startFor, o.id)}
                className="w-full text-left px-1 py-2.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                data-testid={`work-start-for-${o.code}`}>
                <span className="font-mono text-xs text-slate-500">{o.code}</span> {o.name}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartFor(null)} data-testid="work-start-for-cancel">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={switchOpen} onOpenChange={setSwitchOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Pick your task</DialogTitle>
            <DialogDescription>
              The running clock keeps its start time — only the task changes.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {items.filter((i) => !i.completed).map((i) => {
              const current = i.active_session;
              return (
                <button key={`${i.job_id}-${i.machine_id}-${i.operation_id}`} type="button" disabled={busy || current}
                  onClick={() => call(async () => {
                    await api.post(`/work/sessions/${active.id}/switch-task`, {
                      job_id: i.job_id, machine_id: i.machine_id, operation_id: i.operation_id,
                    });
                    setSwitchOpen(false);
                    toast.success("Task switched");
                  })}
                  className={`w-full text-left px-1 py-2.5 hover:bg-slate-50 disabled:opacity-50 ${current ? "bg-slate-50" : ""}`}
                  data-testid={`work-switch-to-${i.machine_code}-${i.operation_code}`}>
                  <div className="text-sm text-slate-900">{i.machine_code} · {i.operation_name}</div>
                  <div className="text-xs text-slate-500">
                    {i.job_code} · planned {i.machine_planned_hours || 0} h{current ? " · current task" : ""}
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwitchOpen(false)} data-testid="work-switch-cancel">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pauseOpen} onOpenChange={(v) => !v && setPauseOpen(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Pause work</DialogTitle>
            <DialogDescription>
              The paused time is removed from your hours only after your manager approves the reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <EditableSelect optionsKey="downtime_reason" value={pauseReason} onChange={setPauseReason}
              placeholder="Select reason..." testId="work-pause-reason" />
            <Textarea value={pauseNote} onChange={(e) => setPauseNote(e.target.value)}
              placeholder="Optional detail for the manager" data-testid="work-pause-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPauseOpen(null)} data-testid="work-pause-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={doPause} disabled={busy} data-testid="work-pause-confirm">
              Pause now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
